// Display · Pill
//
// Canonical tone-coded inline status pill.
//
// Atom — no events, no configurable affordances. The Output and Actions
// interfaces are dropped per the @ds atom convention (Rule 3 / ds-engineer.md).
//
// All visual values come from `@ds/tokens/palette`.

import { useState, type MouseEvent, type ReactNode } from "react";
import {
  CHIP_PAD,
  FONT_SIZE,
  FONT_WEIGHT,
  LETTER_SPACING,
  LINE_HEIGHT,
  MOTION,
  RADIUS,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export type PillTone = ToneName;
export type PillSize = "sm" | "md";
export type PillVariant = "soft" | "outline";

export interface PillInput {
  /**
   * Semantic tone (color family). Drives background, text, and border color
   * from the TONE ramp. Required — the tone is the primary signal.
   */
  tone: PillTone;
  /**
   * Visual variant.
   * - `"soft"` (default) — filled soft background + transparent border.
   * - `"outline"` — transparent background + colored border.
   */
  variant?: PillVariant;
  /**
   * Size scale.
   * - `"sm"` — dense row contexts (table cells, condensed lists). font-size 11.
   * - `"md"` (default) — section headers, hero bands. font-size 12.
   */
  size?: PillSize;
  /** Optional leading icon node (e.g. a 12×12 SVG or Ant Design icon). */
  icon?: ReactNode;
  /** The pill label text. */
  children: ReactNode;
  /** `data-testid` on the root `<span>`. Atoms do NOT auto-emit inner testids. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface PillOutput {
  /**
   * Optional click handler. When provided the pill becomes interactive —
   * gains pointer cursor + soft hover transition. Atoms stay non-interactive
   * by default; pass `onClick` only for clickable affordances (e.g. inline
   * editable selection openers).
   */
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
}

export type PillProps = PillInput & PillOutput;

// ─── Implementation ──────────────────────────────────────────────────────

export function Pill({
  tone,
  variant = "soft",
  size = "md",
  icon,
  children,
  testId,
  onClick,
}: PillProps) {
  const t = TONE[tone];
  const isOutline = variant === "outline";
  const isClickable = !!onClick;
  const [hovered, setHovered] = useState(false);

  // Hover bg: deepen the tone one step. Soft variant → darker `soft` ramp;
  // outline variant → fade in the `softBg` wash + tint border to the base.
  const hoverBg     = isOutline ? t.softBg : t.soft;
  const hoverBorder = isOutline ? t.base   : t.border;

  return (
    <span
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={isClickable ? () => setHovered(true)  : undefined}
      onMouseLeave={isClickable ? () => setHovered(false) : undefined}
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           CHIP_PAD.iconGap,
        background:    isClickable && hovered
                         ? hoverBg
                         : (isOutline ? "transparent" : t.softBg),
        color:         t.text,
        border:        `1px solid ${
                         isClickable && hovered
                           ? hoverBorder
                           : (isOutline ? t.border : "transparent")
                       }`,
        fontSize:      size === "sm" ? FONT_SIZE.label : FONT_SIZE.small,
        fontWeight:    FONT_WEIGHT.semibold,
        padding:       size === "sm" ? CHIP_PAD.sm : CHIP_PAD.md,
        borderRadius:  RADIUS.pill,
        lineHeight:    LINE_HEIGHT.relaxed,
        letterSpacing: LETTER_SPACING.chip,
        whiteSpace:    "nowrap",
        cursor:        isClickable ? "pointer" : undefined,
        transition:    isClickable
                         ? `background ${MOTION.fast} ease, border-color ${MOTION.fast} ease`
                         : undefined,
        userSelect:    isClickable ? "none" : undefined,
      }}
    >
      {icon}
      {children}
    </span>
  );
}
